if (!window.NineNine) {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE = 0.13;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const hexToRgb = (hex) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
  const mixRgb = (from, to, amount) => from.map((value, index) => Math.round(value + (to[index] - value) * amount));

  const state = {
    observer: null,
    parallaxItems: [],
    liftItems: [],
    blendStops: [],
    main: document.getElementById('MainContent'),
    running: false,
    scrolledSinceFrame: false,
  };

  const observeReveals = () => {
    state.observer?.disconnect();
    const revealElements = [...document.querySelectorAll('.nine-nine .nine-nine-rise, .nine-nine .nine-nine-develop, .nine-nine .nine-nine-reveal-group')];

    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      for (const element of revealElements) element.classList.add('is-in');
      return;
    }

    state.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        state.observer.unobserve(entry.target);
      }
    }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' });

    for (const element of revealElements) {
      if (!element.classList.contains('is-in')) state.observer.observe(element);
    }
  };

  const collect = () => {
    state.parallaxItems = [...document.querySelectorAll('.nine-nine [data-nine-nine-parallax]')].map((element) => ({
      element,
      strength: Number(element.dataset.nineNineParallax) || 0,
      current: 0,
    }));
    state.liftItems = [...document.querySelectorAll('.nine-nine [data-nine-nine-lift]')].map((element) => ({
      element,
      strength: Number(element.dataset.nineNineLift) || 0,
      current: 0,
    }));

    state.blendStops = [...document.querySelectorAll('.nine-nine[data-nine-nine-bg]')].map((element) => ({
      element,
      rgb: hexToRgb(element.dataset.nineNineBg),
    }));

    state.main?.classList.toggle('nine-nine-blend', state.blendStops.length > 1);
  };

  const updateBlend = (viewportHeight) => {
    if (!state.main || state.blendStops.length < 2) return;
    let color = state.blendStops[0].rgb;

    for (const stop of state.blendStops.slice(1)) {
      const { top } = stop.element.getBoundingClientRect();
      const progress = clamp((viewportHeight * 1.05 - top) / (viewportHeight * 0.55), 0, 1);
      color = mixRgb(color, stop.rgb, progress * progress * (3 - 2 * progress));
    }

    state.main.style.setProperty('--nine-nine-page-bg', `rgb(${color.join(',')})`);
  };

  const easeItems = (items, viewportHeight, property, getTarget) => {
    let settled = true;

    for (const item of items) {
      const rect = item.element.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewportHeight + 200) continue;
      const centerOffset = clamp((rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight, -1.2, 1.2);
      const target = getTarget(item, centerOffset, rect);
      item.current += (target - item.current) * EASE;
      if (Math.abs(target - item.current) > 0.1) settled = false;
      item.element.style.setProperty(property, `${item.current.toFixed(2)}px`);
    }

    return settled;
  };

  const frame = () => {
    const viewportHeight = window.innerHeight;
    updateBlend(viewportHeight);

    let settled = true;
    if (!REDUCED_MOTION) {
      const parallaxSettled = easeItems(state.parallaxItems, viewportHeight, '--nine-nine-py', (item, offset, rect) => -offset * item.strength * rect.height);
      const liftSettled = easeItems(state.liftItems, viewportHeight, '--nine-nine-ly', (item, offset) => offset * item.strength * 40);
      settled = parallaxSettled && liftSettled;
    }

    if (settled && !state.scrolledSinceFrame) {
      state.running = false;
      return;
    }

    state.scrolledSinceFrame = false;
    requestAnimationFrame(frame);
  };

  const requestFrame = () => {
    state.scrolledSinceFrame = true;
    if (state.running) return;
    state.running = true;
    requestAnimationFrame(frame);
  };

  const refresh = () => {
    collect();
    observeReveals();
    requestFrame();
  };

  window.NineNine = { refresh };

  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
}
